import { useRef } from 'react';

import { UserFlow } from '@constants/generic';
import { STORAGE_KEYS } from '@constants/storage';
import {
  KycDetailsFlowContext,
  MixpanelEventV2,
  sendMixpanelEventV2
} from '@core/analytics/mixpanel';
import { getLocalStorageItem } from '@utils/storage';

export type KycAnalyticsBase = {
  flowContext: KycDetailsFlowContext;
  profileName?: string;
  businessName?: string;
  mobileNumber?: string;
};

export type PanGstType = 'PAN' | 'GST';

export const getOnboardingKycFlowContext = (
  userFlow: string
): KycDetailsFlowContext =>
  userFlow === UserFlow.UPGRADE_TO_ORG
    ? 'upgrade_to_org'
    : 'initial_onboarding';

export const buildSelfDeclarationAnalytics = (
  user?: { name?: string; mobile?: string },
  org?: { name?: string }
) => ({
  profileName: user?.name || '',
  businessName: org?.name || '',
  mobileNumber: user?.mobile || getLocalStorageItem(STORAGE_KEYS.MOBILE) || ''
});

const getIdentityProps = (analytics: KycAnalyticsBase) => ({
  mobile_number:
    analytics.mobileNumber || getLocalStorageItem(STORAGE_KEYS.MOBILE) || '',
  country_code: '91',
  flow_context: analytics.flowContext
});

const getProfileProps = (analytics: KycAnalyticsBase) => ({
  ...getIdentityProps(analytics),
  profile_name: analytics.profileName || '',
  business_name: analytics.businessName || ''
});

const withFailureReason = (
  status: 'success' | 'failed',
  failureReason?: string
) =>
  status === 'failed' && failureReason ? { failure_reason: failureReason } : {};

export const trackKycDetailsPageViewed = (analytics: KycAnalyticsBase) => {
  sendMixpanelEventV2(
    MixpanelEventV2.KYC_DETAILS_PAGE_VIEWED,
    getIdentityProps(analytics)
  );
};

export const trackKycDetailsEnteredAndContinued = (
  analytics: KycAnalyticsBase,
  params: { updatedIndividualToOrgAccount: boolean }
) => {
  sendMixpanelEventV2(MixpanelEventV2.KYC_DETAILS_ENTERED_AND_CONTINUED, {
    ...getProfileProps(analytics),
    updated_Individual_to_Org_account: params.updatedIndividualToOrgAccount
  });
};

export const trackKycDocumentsPageViewed = (analytics: KycAnalyticsBase) => {
  sendMixpanelEventV2(
    MixpanelEventV2.KYC_DOCUMENTS_PAGE_VIEWED,
    getProfileProps(analytics)
  );
};

export const trackKycDocumentsUploadedAndContinued = (
  analytics: KycAnalyticsBase,
  params: {
    panGst: PanGstType;
    licenseType?: string;
    status: 'success' | 'failed';
    failureReason?: string;
  }
) => {
  sendMixpanelEventV2(MixpanelEventV2.KYC_DOCUMENTS_UPLOADED_AND_CONTINUED, {
    ...getProfileProps(analytics),
    'PAN/GST': params.panGst,
    license_type: params.licenseType || '',
    status: params.status,
    ...withFailureReason(params.status, params.failureReason)
  });
};

export const trackPanGstFileAttached = (
  analytics: KycAnalyticsBase,
  params: {
    type: PanGstType;
    status: 'success' | 'failed';
    failureReason?: string;
  }
) => {
  sendMixpanelEventV2(MixpanelEventV2.PAN_GST_FILE_ATTACHED, {
    ...getProfileProps(analytics),
    type: params.type,
    status: params.status,
    ...withFailureReason(params.status, params.failureReason)
  });
};

export const trackPanGstFileDeleted = (
  analytics: KycAnalyticsBase,
  params: { type: PanGstType }
) => {
  sendMixpanelEventV2(MixpanelEventV2.PAN_GST_FILE_DELETED, {
    ...getProfileProps(analytics),
    type: params.type
  });
};

export const trackBusinessLicenseAttached = (
  analytics: KycAnalyticsBase,
  params: {
    licenseType: string;
    status: 'success' | 'failed';
    failureReason?: string;
  }
) => {
  sendMixpanelEventV2(MixpanelEventV2.BUSINESS_LICENSE_ATTACHED, {
    ...getProfileProps(analytics),
    license_type: params.licenseType,
    status: params.status,
    ...withFailureReason(params.status, params.failureReason)
  });
};

export const trackBusinessLicenseDeleted = (
  analytics: KycAnalyticsBase,
  params: { licenseType: string }
) => {
  sendMixpanelEventV2(MixpanelEventV2.BUSINESS_LICENSE_DELETED, {
    ...getProfileProps(analytics),
    license_type: params.licenseType
  });
};

export const trackKycBankDetailsPageViewed = (analytics: KycAnalyticsBase) => {
  sendMixpanelEventV2(
    MixpanelEventV2.KYC_BANK_DETAILS_PAGE_VIEWED,
    getProfileProps(analytics)
  );
};

export const trackKycBankVerificationAttempted = (
  analytics: KycAnalyticsBase,
  params: {
    status: 'success' | 'failed';
    verifiedCount: number;
    verifiedBankName?: string;
    verifiedName?: string;
    failureReason?: string;
  }
) => {
  sendMixpanelEventV2(MixpanelEventV2.KYC_BANK_VERIFICATION_ATTEMPTED, {
    ...getProfileProps(analytics),
    status: params.status,
    verified_bank_name: params.verifiedBankName || '',
    verified_name: params.verifiedName || '',
    verified_count: params.verifiedCount,
    ...withFailureReason(params.status, params.failureReason)
  });
};

export const trackKycBankDetailsEdited = (
  analytics: KycAnalyticsBase,
  params: {
    editCount: number;
    verifiedBankName?: string;
    verifiedName?: string;
  }
) => {
  sendMixpanelEventV2(MixpanelEventV2.KYC_BANK_DETAILS_EDITED, {
    ...getProfileProps(analytics),
    verified_bank_name: params.verifiedBankName || '',
    verified_name: params.verifiedName || '',
    edit_count: params.editCount
  });
};

type BankVerificationDetails = {
  verifiedBankName?: string;
  verifiedName?: string;
  failureReason?: string;
};

// Tracks each bank verification attempt, maintaining its own attempt counter
// so BankInfo.tsx and SubmitKycModal.tsx don't each need their own ref + wiring.
export const useBankVerificationTracker = (
  getAnalytics: () => KycAnalyticsBase,
  enabled = true
) => {
  const countRef = useRef(0);

  return (status: 'success' | 'failed', details?: BankVerificationDetails) => {
    if (!enabled) return;
    countRef.current += 1;
    trackKycBankVerificationAttempted(getAnalytics(), {
      status,
      verifiedCount: countRef.current,
      verifiedBankName: details?.verifiedBankName,
      verifiedName: details?.verifiedName,
      failureReason: details?.failureReason
    });
  };
};

type BankEditDetails = {
  verifiedBankName?: string;
  verifiedName?: string;
};

// Same idea as useBankVerificationTracker, for the "bank details edited" event.
export const useBankEditTracker = (
  getAnalytics: () => KycAnalyticsBase,
  enabled = true
) => {
  const countRef = useRef(0);

  return (details: BankEditDetails) => {
    if (!enabled) return;
    countRef.current += 1;
    trackKycBankDetailsEdited(getAnalytics(), {
      editCount: countRef.current,
      verifiedBankName: details.verifiedBankName,
      verifiedName: details.verifiedName
    });
  };
};

export const trackKycSubmitted = (
  analytics: KycAnalyticsBase,
  params: {
    status: 'success' | 'failed';
    failureReason?: string;
    attemptNumber?: number;
  }
) => {
  sendMixpanelEventV2(MixpanelEventV2.KYC_SUBMITTED, {
    ...getProfileProps(analytics),
    status: params.status,
    ...withFailureReason(params.status, params.failureReason),
    ...(analytics.flowContext === 'resubmission' && params.attemptNumber != null
      ? { attempt_number: params.attemptNumber }
      : {})
  });
};

const getSelfDeclarationProps = (analytics: {
  profileName?: string;
  businessName?: string;
  mobileNumber?: string;
}) => ({
  mobile_number:
    analytics.mobileNumber || getLocalStorageItem(STORAGE_KEYS.MOBILE) || '',
  country_code: '91',
  // TODO: Populate flow_context from backend when available
  flow_context: '',
  profile_name: analytics.profileName || '',
  business_name: analytics.businessName || ''
});

export const trackSelfDeclarationFormOpened = (analytics: {
  profileName?: string;
  businessName?: string;
  mobileNumber?: string;
}) => {
  sendMixpanelEventV2(
    MixpanelEventV2.SELF_DECLARATION_FORM_OPENED,
    getSelfDeclarationProps(analytics)
  );
};

export const trackSelfDeclarationFormSubmitted = (analytics: {
  profileName?: string;
  businessName?: string;
  mobileNumber?: string;
}) => {
  sendMixpanelEventV2(
    MixpanelEventV2.SELF_DECLARATION_FORM_SUBMITTED,
    getSelfDeclarationProps(analytics)
  );
};
